import { isNullOrUndefined } from "../../../../../../../tools";
import { IdProvider } from "../../../../../../services/idprovider.service";
import { ServerCommunicatorService } from "../../../../../../services/server-communicator.service";
import { UIUtils } from "../../../../../../services/uiutils.service";
import { Utils } from "../../../../../../services/utils.service";
import { DataContainer } from "../../../../../infrastructure/datacontainer";
import { DataContainerService } from "../../../../../infrastructure/datacontainer.service";
import { RequestTypes } from "../../../../../infrastructure/enums";
import { IPersistable } from "../../../../../infrastructure/IPersistable";
import { PayloadPacketFacade } from "../../../../../infrastructure/payloadpacket/payloadpacketfacade";
import { TransportData } from "../../../../../infrastructure/transportdata";
import { ValidationResultAccumulator } from "../../../../../infrastructure/validationresultaccumulator";
import { ValidationMessages, ValidationPatterns } from "../../../../constants";
import { DistrictFetchRequest } from "./districtfetchrequest";

export class DistrictProps {

  public created_date: string = '';
  public updated_date: string = '';
  public created_by: number = 0;
  public updated_by: number = 0;

  public id: number = 0;
  public name_en: string = '';
  public name_mr: string = '';
  public district_code: number = 0;
  public from_month: number = 0;
  public from_year: number = 0;
  public to_month: number = 0;
  public to_year: number = 0;
  public state_id: number = 0;
  public readonly state_name_en: string = '';
  public readonly state_name_mr: string = '';
  public zone_id: number = 0;
  public readonly zone_name: string = '';
  public is_change_zone_applicable: number = 0;
  public month_indicator: string = '';
  public trans_date_time: string = '';

  public readonly is_newly_created: boolean = false;


  private constructor(IsNewlyCreated: boolean) {
    this.is_newly_created = IsNewlyCreated;
  }

  public static Blank() {
    return new DistrictProps(true);
  }
}

export class District implements IPersistable<District> {
  public static readonly mastertablename: string = 'district_master';

  private constructor(public readonly p: DistrictProps, public readonly AllowEdit: boolean) {

  }

  public async EnsurePrimaryKeysWithValidValues(): Promise<void> {
    if (this.p.id === undefined || this.p.id === 0) {
      const newids = await IdProvider.GetInstance().GetNextEntityId();
      // const newids = await IdProvider.GetInstance().GetAllocateSingleIds();
      this.p.id = newids[0];
      if (this.p.id <= 0) throw new Error("Cannot assign Id. Please try again");
    }
  }

  public GetEditableVersion(): District {
    let newDistrict: DistrictProps = Utils.GetInstance().DeepCopy(this.p);
    return District.CreateInstance(newDistrict, true);
  }

  public static CreateNewInstance() {
    return new District(DistrictProps.Blank(), true);
  }

  public static CreateInstance(data: any, allowEdit: boolean) {
    return new District(data as DistrictProps, allowEdit);
  }

  public CheckSaveValidity(_td: TransportData, vra: ValidationResultAccumulator): void {
    if (!this.AllowEdit) vra.add('', 'This object is not editable and hence cannot be saved.');

  }

  public MergeIntoTransportData(td: TransportData) {
    DataContainerService.GetInstance().MergeIntoContainer(td.MainData, District.mastertablename, this.p);
  }

  private static m_currentInstance: District = District.CreateNewInstance();

  public static GetCurrentInstance() {
    return District.m_currentInstance;
  }

  public static SetCurrentInstance(value: District) {
    District.m_currentInstance = value;
  }

  // ********************************************
  public static cacheDataChangeLevel: number = -1;

  public static SingleInstanceFromTransportData(td: TransportData): District {
    let dcs = DataContainerService.GetInstance();
    if (dcs.CollectionExists(td.MainData, District.mastertablename)) {
      for (let data of dcs.GetCollection(td.MainData, District.mastertablename)!.Entries) {
        return District.CreateInstance(data, false);
      }
    }

    return null as any;
  }

  public static ListFromDataContainer(cont: DataContainer,
    filterPredicate: (arg0: any) => boolean = null as any,
    sortPropertyName: string = "Name"): District[] {
    let result: District[] = [];

    let dcs = DataContainerService.GetInstance();

    if (dcs.CollectionExists(cont, District.mastertablename)) {
      let coll = dcs.GetCollection(cont, District.mastertablename)!;
      let entries = coll.Entries;

      if (!isNullOrUndefined(filterPredicate)) entries = entries.filter(filterPredicate);

      if (sortPropertyName.trim().length > 0) {
        entries = Utils.OrderByPropertyName(entries, sortPropertyName);
      }

      for (let data of entries) {
        result.push(District.CreateInstance(data, false));
      }
    }

    return result;
  }

  public static ListFromTransportData(td: TransportData): District[] {
    return District.ListFromDataContainer(td.MainData);
  }

  public static async FetchTransportData(req: DistrictFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = req.FormulateTransportData();
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
      return null;
    }

    let tdResponse = JSON.parse(tr.Tag) as TransportData;
    return tdResponse;
  }



    // Function created For Trasaction Service filters 
    public static async FetchTransportDataForTransactionFilter(req: DistrictFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
      let tdRequest = req.FormulateTransportData();
      let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);
  
      let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
      if (!tr.Successful) {
        if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
        return null;
      }
  
      let tdResponse = JSON.parse(tr.Tag) as TransportData;
      return tdResponse;
    }

  public static async FetchInstance(id: number, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new DistrictFetchRequest();
    req.district_ids.push(id);

    let tdResponse = await District.FetchTransportData(req, errorHandler) as TransportData;
    return District.SingleInstanceFromTransportData(tdResponse);
  }

  public static async FetchList(req: DistrictFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdResponse = await District.FetchTransportData(req, errorHandler) as TransportData;
    return District.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireList(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new DistrictFetchRequest();
    let tdResponse = await District.FetchTransportData(req, errorHandler) as TransportData;
    return District.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListByWithZone(zone_id: number, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new DistrictFetchRequest();
    req.with_zone = 1;
    req.zone_ids.push(zone_id);
    let tdResponse = await District.FetchTransportData(req, errorHandler) as TransportData;
    return District.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListByWithZoneRefs(zone_ids: number [], errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new DistrictFetchRequest();
    req.with_zone = 1;
    req.zone_ids = zone_ids;
    let tdResponse = await District.FetchTransportData(req, errorHandler) as TransportData;
    return District.ListFromTransportData(tdResponse);
  }


  public static async FetchEntireListByWithoutZone(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new DistrictFetchRequest();
    req.with_zone = 0;
    let tdResponse = await District.FetchTransportData(req, errorHandler) as TransportData;
    return District.ListFromTransportData(tdResponse);
  }

    // get all asha for Trasaction filter service file 
  public static async FetchEntireListForTransactionFilter(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new DistrictFetchRequest();
    let tdResponse = await District.FetchTransportDataForTransactionFilter(req, errorHandler) as TransportData;
    return District.ListFromTransportData(tdResponse);
  }


  public async DeleteInstance(successHandler: () => Promise<void> = null!, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = new TransportData();
    tdRequest.RequestType = RequestTypes.Deletion;

    this.MergeIntoTransportData(tdRequest);
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
    }
    else {
      if (!isNullOrUndefined(successHandler)) await successHandler();
    }
  }

}
